import { useState, useEffect } from 'react'
import { useLocale } from '../../App'

interface LeaderboardViewProps {
  authToken: string | null
  onBack: () => void
}

interface LBEntry {
  rank: number
  userId: string
  username: string
  photoUrl: string
  winRate: number
  totalGames: number
  wins: number
}

export function LeaderboardView({ authToken, onBack }: LeaderboardViewProps) {
  const { t } = useLocale()
  const [entries, setEntries] = useState<LBEntry[]>([])
  const [myGlory, setMyGlory] = useState<any>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!authToken) return
    Promise.all([
      fetch('/api/leaderboard?sortBy=winRate&limit=10', {
        headers: { Authorization: `Bearer ${authToken}` },
      }).then((r) => r.json()),
      fetch('/api/user/glory', {
        headers: { Authorization: `Bearer ${authToken}` },
      }).then((r) => r.json()),
    ]).then(([lb, glory]) => {
      setEntries(lb.entries || [])
      setMyGlory(glory)
    }).catch(() => {}).finally(() => setLoading(false))
  }, [authToken])

  const RANK_STYLES = [
    { label: '🥇', bg: 'rgba(255,215,0,0.08)', border: 'rgba(255,215,0,0.25)', text: 'text-yellow-300' },
    { label: '🥈', bg: 'rgba(192,192,192,0.06)', border: 'rgba(192,192,192,0.2)', text: 'text-slate-300' },
    { label: '🥉', bg: 'rgba(205,127,50,0.06)', border: 'rgba(205,127,50,0.2)', text: 'text-amber-600' },
  ]

  const myRank = entries.findIndex((e) => e.userId === myGlory?.tgId) + 1

  return (
    <div className="flex flex-col h-full overflow-hidden" style={{ background: 'linear-gradient(180deg, #0a0e1a 0%, #0f1628 100%)' }}>
      {/* Header */}
      <div className="shrink-0 relative px-4 pt-3 pb-3 border-b border-amber-500/20 text-center" style={{ background: 'linear-gradient(135deg, rgba(180,120,50,0.1), rgba(180,120,50,0.02))' }}>
        <button onClick={onBack} className="absolute left-4 top-3 text-amber-400/80 hover:text-amber-300 text-xl">{t('app.back')}</button>
        <div className="text-xl">🏆</div>
        <h2 className="text-lg font-bold text-amber-100/90 tracking-wide">{t('app.leaderboard_title')}</h2>
        <p className="text-[10px] text-amber-400/40 tracking-widest">WINNER LEADERBOARD</p>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto px-4 space-y-2 py-4">
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="w-6 h-6 border-2 border-amber-500/30 border-t-amber-400 rounded-full animate-spin" />
          </div>
        ) : entries.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-16 text-amber-400/30">
            <span className="text-4xl">🏆</span>
            <span className="text-sm">{t('app.leaderboard_empty')}</span>
            <span className="text-[11px]">{t('app.leaderboard_3_plus')}</span>
          </div>
        ) : entries.map((e, i) => {
          const style = RANK_STYLES[i] || { label: `#${e.rank}`, bg: 'rgba(255,255,255,0.02)', border: 'rgba(255,255,255,0.05)', text: 'text-slate-400' }
          return (
            <div
              key={e.userId}
              className="rounded-xl px-4 py-3 border backdrop-blur-sm"
              style={{ background: style.bg, borderColor: style.border }}
            >
              <div className="flex items-center gap-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${style.text}`}>
                  {style.label}
                </div>
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-amber-700/30 to-amber-900/30 flex items-center justify-center ring-1 ring-amber-500/20 overflow-hidden">
                  {e.photoUrl ? (
                    <img src={e.photoUrl} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-lg">👤</span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className={`text-sm font-semibold truncate ${style.text}`}>{e.username}</div>
                  <div className="text-[11px] text-amber-400/30">{t('app.leaderboard_stats', { total: String(e.totalGames), wins: String(e.wins) })}</div>
                </div>
                <div className="text-right">
                  <div className={`text-base font-extrabold tabular-nums ${style.text}`}>
                    {(e.winRate * 100).toFixed(1)}%
                  </div>
                  <div className="text-[10px] text-amber-400/30">{t('app.leaderboard_winrate')}</div>
                </div>
              </div>
              {i === 0 && (
                <div className="mt-2 text-[10px] text-center text-yellow-500/50 tracking-widest">{t('app.leaderboard_champion')}</div>
              )}
            </div>
          )
        })}
      </div>

      {/* My rank - fixed bottom */}
      {myGlory && (
        <div className="shrink-0 px-4 py-3 border-t border-amber-500/20" style={{ background: 'linear-gradient(0deg, rgba(180,120,50,0.08), transparent)' }}>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-600/40 to-blue-800/40 flex items-center justify-center ring-2 ring-blue-500/30 overflow-hidden">
              {myGlory.photoUrl ? (
                <img src={myGlory.photoUrl} alt="" className="w-full h-full object-cover" />
              ) : (
                <span className="text-lg">👤</span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-blue-300 truncate">{myGlory.firstName || myGlory.username || t('app.leaderboard_me')}</div>
              <div className="text-[11px] text-amber-400/30">{t('app.leaderboard_winrate')} {(myGlory.winRate * 100).toFixed(1)}%</div>
            </div>
            <div className="text-right">
              <div className="text-sm font-bold text-amber-400">
                #{myRank > 0 ? myRank : '-'}
              </div>
              <div className="text-[10px] text-amber-400/30">{t('app.leaderboard_rank')}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
